// SPDX-FileCopyrightText: 2026 Mads Ringblom
//
// SPDX-License-Identifier: GPL-3.0-or-later

import type { RefObject } from 'react';

import type { DraftValidate } from '../../../app/textDraft';
import { fieldKeys, useTextDraft } from '../../../app/useTextDraft';
import { formatQuantityInput, parseDanishNumber } from '../../../kortlaegning/vocab';
import { TextInput } from '../../TextInput';

export interface NumberWithUnitProps {
  label: string;
  value: number | null;
  unit: string;
  /** Emptying the field sends null (tons); otherwise an empty draft snaps back. */
  clearable?: boolean;
  readOnly?: boolean;
  placeholder?: string;
  onCommit: (value: number | null) => void;
  /** The draft did not parse; the field has snapped back. */
  onInvalid?: (label: string) => void;
  /** Where focus goes after Enter/Esc (the inspector root). */
  home: RefObject<HTMLElement | null>;
}

/**
 * A number in Danish form with its unit (v8 `num()`): commits on Enter or
 * blur, Esc reverts. `1.240,5` parses as 1240,5; an untouched blur sends
 * nothing (`useTextDraft`).
 */
export function NumberWithUnit({ label, value, unit, clearable, readOnly, placeholder, onCommit, onInvalid, home }: NumberWithUnitProps) {
  const validate: DraftValidate<number | null> = (draft) => {
    if (draft.trim() === '') return clearable ? { value: null } : null;
    const n = parseDanishNumber(draft);
    return n === null ? null : { value: n };
  };
  const draft = useTextDraft<number | null>(value === null ? '' : formatQuantityInput(value), onCommit, {
    validate,
    onInvalid: () => onInvalid?.(label),
  });
  const keys = fieldKeys(draft, home);
  return (
    <TextInput
      variant="filled"
      size="lg"
      block
      mono
      suffix={unit}
      type="text"
      inputMode="decimal"
      aria-label={label}
      placeholder={placeholder}
      readOnly={readOnly}
      {...draft.props}
      onKeyDown={(e) => {
        keys(e);
        if (e.key === 'Enter' && e.defaultPrevented) home.current?.focus({ preventScroll: true });
      }}
    />
  );
}
